import { forwardRef, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import Input, { InputProps } from './Input'
import { cn } from '../../lib/utils'

export type PasswordInputProps = Omit<InputProps, 'type' | 'endAdornment'>

/**
 * Password field with a reveal toggle.
 *
 * NIST SP 800-63B 5.1.1.2 recommends letting the user display the secret while
 * typing — masked-only entry is a significant source of failed logins, and
 * these are 12+ character passwords.
 */
const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, disabled, ...props }, ref) => {
    const [visible, setVisible] = useState(false)

    return (
      <Input
        ref={ref}
        type={visible ? 'text' : 'password'}
        disabled={disabled}
        // Keeps the value clear of the toggle at the right edge.
        className={cn('pr-11', className)}
        endAdornment={
          <button
            // Without this a bare <button> in a <form> defaults to submit, so
            // every reveal would fire the form.
            type="button"
            onClick={() => setVisible((v) => !v)}
            disabled={disabled}
            aria-label={visible ? 'Hide password' : 'Show password'}
            aria-pressed={visible}
            aria-controls={props.id}
            className="flex h-full w-11 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50"
          >
            {visible ? (
              <EyeOff className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Eye className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        }
        {...props}
      />
    )
  }
)

PasswordInput.displayName = 'PasswordInput'

export default PasswordInput
export { PasswordInput }
